import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Copy, Loader2 } from 'lucide-react';
import { PorterAvatar, StatusPill, ymd, addDays, fmtDay, errMsg } from './porterUi';
import { CrewJobCard } from './CrewJobCard';
import { DriverChipSmall } from './DriverChipSmall';

const SUMMARY = [
  ['total_porters', 'Total porters'], ['working', 'Working today'], ['available', 'Available'],
  ['absent', 'Absent'], ['fully_staffed', 'Fully staffed jobs'], ['understaffed', 'Understaffed jobs'],
];

export const CrewBoard = ({ authFetch, date, setDate }) => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [onlyFree, setOnlyFree] = useState(true);

  const load = useCallback(() => {
    authFetch('get', `/admin/crew-board?start=${date}&days=1`).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e, "Couldn't load crew board.")));
  }, [authFetch, date]);
  useEffect(() => { load(); }, [load]);

  const copyPrev = async () => {
    const from = addDays(date, -1);
    if (!window.confirm(`Copy each driver's porters from ${fmtDay(from)} onto ${fmtDay(date)}?`)) return;
    try {
      const { data: r } = await authFetch('post', '/admin/crew/copy', { from_date: from, to_date: date });
      toast.success(`Copied ${r.copied} assignment${r.copied === 1 ? '' : 's'}.`);
      if (r.skipped.length) toast.warning(`Skipped: ${r.skipped.join('; ')}`, { duration: 8000 });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" />;
  const st = data.porter_status[date] || {};
  const pool = data.porters.filter(p => p.employment_status === 'active' && (!onlyFree || st[p.id] === 'available'));
  const byDriver = [...data.drivers, { id: null, name: 'Unassigned driver' }].map(d => ({ d, jobs: data.jobs.filter(j => (j.driver_id || null) === d.id) })).filter(g => g.jobs.length);

  return (
    <div data-testid="crew-board">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => setDate(addDays(date, -1))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Previous day" data-testid="crew-prev"><ChevronLeft className="w-4 h-4" /></button>
        <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="crew-date" />
        <button onClick={() => setDate(addDays(date, 1))} className="h-10 w-10 rounded-md border border-slate-200 bg-white inline-flex items-center justify-center" aria-label="Next day" data-testid="crew-next"><ChevronRight className="w-4 h-4" /></button>
        <button onClick={() => setDate(ymd(new Date()))} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-xs font-bold" data-testid="crew-today">Today</button>
        <button onClick={copyPrev} className="h-10 px-3 rounded-md border border-slate-200 bg-white text-xs font-bold inline-flex items-center gap-1 ml-auto" data-testid="crew-copy-prev"><Copy className="w-3.5 h-3.5" />Copy previous day</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-5">
        {SUMMARY.map(([k, l]) => (
          <div key={k} className="rounded-xl bg-white border border-slate-200 p-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{l}</div>
            <div className={`font-display text-2xl font-black ${k === 'understaffed' && data.summary[k] ? 'text-amber-600' : 'text-[color:var(--mv-navy)]'}`} data-testid={`crew-sum-${k}`}>{data.summary[k]}</div>
          </div>
        ))}
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-4">
        <aside className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 lg:sticky lg:top-4 self-start" data-testid="crew-pool">
          <div className="flex items-center justify-between mb-2">
            <strong className="text-sm text-[color:var(--mv-navy)]">Porters</strong>
            <label className="text-[11px] text-slate-500 inline-flex items-center gap-1"><input type="checkbox" checked={onlyFree} onChange={(e) => setOnlyFree(e.target.checked)} data-testid="crew-only-free" />Available only</label>
          </div>
          <p className="text-[11px] text-slate-400 mb-2">Drag onto a job, or tap to select then tap "Assign here".</p>
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-1">
            {pool.length === 0 && <div className="text-xs text-slate-500">No porters to show.</div>}
            {pool.map(p => (
              <button
                key={p.id}
                type="button"
                draggable
                onDragStart={(e) => e.dataTransfer.setData('application/json', JSON.stringify({ porterId: p.id, name: p.name }))}
                onClick={() => setSelected(selected?.id === p.id ? null : p)}
                className={`shrink-0 min-w-[180px] lg:min-w-0 flex items-center gap-2 rounded-md border px-2 py-2 text-left cursor-grab ${selected?.id === p.id ? 'border-[color:var(--mv-orange)] bg-orange-50' : 'border-slate-200'}`}
                data-testid={`crew-pool-${p.id}`}
              >
                <PorterAvatar p={p} size={28} />
                <span className="flex-1 min-w-0 text-sm font-bold text-[color:var(--mv-navy)] truncate">{p.name}</span>
                <StatusPill status={st[p.id]} />
              </button>
            ))}
          </div>
        </aside>
        <div className="space-y-4 min-w-0">
          {byDriver.length === 0 && <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="crew-empty">No booked jobs on {fmtDay(date)}.</div>}
          {byDriver.map(({ d, jobs }) => (
            <section key={d.id || 'none'} className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid={`crew-driver-${d.id || 'none'}`}>
              <DriverChipSmall d={d} jobs={jobs} />
              <div className="grid md:grid-cols-2 gap-3 mt-2">
                {jobs.map(j => <CrewJobCard key={j.id} job={j} vans={data.vans} statusMap={st} authFetch={authFetch} reload={load} selectedPorter={selected} onAssigned={() => setSelected(null)} />)}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
};
